import Fragment from 'ember-data-model-fragments/fragment';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr } from '@warp-drive/legacy/model';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type DataType from '@linc-technologies/linced-common/enums/data-type';
import type InputType from '@linc-technologies/linced-common/enums/input-type';
import type Value from '@linc-technologies/linced-common/models/dynamic-field/value';
import type FieldOption from '@linc-technologies/linced-common/models/people-schema/field-option';

// Proto: enrolments.Field.
export default class Field extends Fragment {
  declare [Type]: 'enrolments/field';

  // fieldId is the unique dynamic field identifier.
  @attr('string', { defaultValue: '' })
  declare fieldId: string;

  // index is the relative field position when presented to the user.
  @attr('number', { defaultValue: 0 })
  declare index: number;

  // isSiblingLinked, if enabled, updates the field data across known siblings.
  @attr('boolean', { defaultValue: false })
  declare isSiblingLinked: boolean;

  // key is the unique identifier of the field for the backend.
  @attr('string', { defaultValue: '' })
  declare key: string;

  // inputType informs the frontend how to display the form element.
  @attr('number', { defaultValue: 0 })
  declare inputType: EnumValue<typeof InputType> | 0;

  // dataType provides metadata around the specific type the data is.
  @attr('number', { defaultValue: 0 })
  declare dataType: EnumValue<typeof DataType> | 0;

  // label is the name of the field that is displayed to the end user.  For example, 'Address 1'
  @attr('string', { defaultValue: '' })
  declare label: string;

  // default is the value the element should be on load.
  @fragment('dynamic-field/value', { defaultValue: () => ({}) })
  declare default: Value | null;

  // placeholder is the text to show in the input element as an example to the user.
  @attr('string', { defaultValue: '' })
  declare placeholder: string;

  // options provides a way to hold valid options for checkboxes, radio and select dropdowns.
  @fragmentArray('people-schema/field-option')
  declare options: FragmentArray<FieldOption>;

  // optionsFromEnum flags the field as using a front-end enum for the list of potential options.
  @attr('boolean', { defaultValue: false })
  declare optionsFromEnum: boolean;

  // htmlAttributes provides a way to dynamically inject HTML attributes on an HTML element.
  @attr()
  declare htmlAttributes: unknown;

  // isRequired, if enabled, flags the field as required/mandatory.
  @attr('boolean', { defaultValue: false })
  declare isRequired: boolean;

  // staticContent enables this field option to contain static content, for example,
  // a description or title relating to the following fields in a list.
  @attr('string', { defaultValue: '' })
  declare staticContent: string;

  // isObsolete, if enabled, flags the field as obsolete
  // (not shown on enrolment form but visible to school)
  @attr('boolean', { defaultValue: false })
  declare isObsolete: boolean;
}
